// Action menus (row "…" menus, the account menu, "Color by"): a trigger button plus a WAI-ARIA menu. Keyboard:
// Enter/Space/ArrowDown on the trigger opens on the first item (ArrowUp on the last), arrows move, Home/End jump,
// typing a letter jumps to the next item starting with it, Escape closes and returns focus, Tab closes. The menu is
// a top-layer popover positioned next to the trigger (ui/position.ts), so table and card overflow never clips it.

import {
  cloneElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import { Check } from 'lucide-react';
import { cn } from './cn';
import { focusIfConnected } from './focus';
import { useOutsidePointerDown } from './hooks';
import { hidePopoverSafe, showPopoverSafe, useAnchoredPosition, type Placement } from './position';

export interface MenuActionItem {
  type?: 'item';
  /** Stable key; defaults to the index. */
  id?: string;
  label: ReactNode;
  /** Plain text for typeahead when `label` is not a string. */
  textValue?: string;
  icon?: ReactNode;
  /** Secondary line under the label. */
  description?: ReactNode;
  /** Right-aligned hint ("⌘K", a count). */
  shortcut?: ReactNode;
  /** Makes it a menuitemcheckbox with a check mark (Color by, column pickers). */
  checked?: boolean;
  /** With `checked`: one choice of several (Appearance), announced as a menuitemradio instead. */
  radio?: boolean;
  destructive?: boolean;
  disabled?: boolean;
  /** Keep the menu open after selecting (multi-toggle lists). */
  keepOpen?: boolean;
  onSelect: () => void;
}

export interface MenuSeparator {
  type: 'separator';
  id?: string;
}

export interface MenuGroupLabel {
  type: 'label';
  id?: string;
  label: ReactNode;
  /**
   * Makes the items after it, up to the next separator or label, one role="group" named by this label, so a screen
   * reader says what they are for (a set of radio items next to unrelated items, like Appearance in the account menu).
   */
  group?: boolean;
}

export type MenuEntry = MenuActionItem | MenuSeparator | MenuGroupLabel;

interface TriggerProps {
  ref?: Ref<HTMLElement>;
  id?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  'aria-haspopup'?: 'menu';
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
}

export interface DropdownMenuProps {
  /**
   * The button that opens the menu (a ui Button/IconButton or any element that forwards `ref`, `onClick`,
   * `onKeyDown` and aria props to a focusable element).
   */
  trigger: ReactElement<TriggerProps>;
  items: readonly MenuEntry[];
  /** Accessible name of the menu ("Row actions"); defaults to being labelled by the trigger. */
  label?: string;
  placement?: Placement;
  /** Non-interactive content at the top (the account menu's name and email). */
  header?: ReactNode;
  minWidth?: number;
  className?: string;
  onOpenChange?: (open: boolean) => void;
}

function setRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') ref(value);
  else if (ref) (ref as { current: T | null }).current = value;
}

function isAction(entry: MenuEntry): entry is MenuActionItem {
  return entry.type === undefined || entry.type === 'item';
}

function textOf(entry: MenuActionItem): string {
  if (entry.textValue) return entry.textValue;
  return typeof entry.label === 'string' || typeof entry.label === 'number' ? String(entry.label) : '';
}

export function DropdownMenu({
  trigger,
  items,
  label,
  placement = 'bottom-start',
  header,
  minWidth = 200,
  className,
  onOpenChange,
}: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const [initial, setInitial] = useState<'first' | 'last' | 'none'>('first');
  const triggerRef = useRef<HTMLElement | null>(null);
  const menuId = `menu${useId()}`;
  const triggerId = `${menuId}-trigger`;

  const change = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  const openWith = (focus: 'first' | 'last' | 'none') => {
    setInitial(focus);
    change(true);
  };

  const close = (restoreFocus: boolean) => {
    change(false);
    if (restoreFocus) focusIfConnected(triggerRef.current);
  };

  const triggerProps = trigger.props;
  const triggerEl = cloneElement(trigger, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      setRef(triggerProps.ref, node);
    },
    id: triggerProps.id ?? triggerId,
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? menuId : undefined,
    onClick: (event: MouseEvent<HTMLElement>) => {
      triggerProps.onClick?.(event);
      if (event.defaultPrevented) return;
      // A mouse click opens without moving focus into the menu (detail > 0); keyboard "clicks" focus the first item.
      if (open) close(false);
      else openWith(event.detail === 0 ? 'first' : 'none');
    },
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      triggerProps.onKeyDown?.(event);
      if (event.defaultPrevented) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        openWith(event.key === 'ArrowUp' ? 'last' : 'first');
      }
    },
  });

  return (
    <>
      {triggerEl}
      {open && (
        <MenuPopup
          id={menuId}
          labelledBy={label ? undefined : (triggerProps.id ?? triggerId)}
          label={label}
          items={items}
          header={header}
          placement={placement}
          minWidth={minWidth}
          initialFocus={initial}
          anchorRef={triggerRef}
          className={className}
          onClose={close}
        />
      )}
    </>
  );
}

interface MenuPopupProps {
  id: string;
  label?: string;
  labelledBy?: string;
  items: readonly MenuEntry[];
  header?: ReactNode;
  placement: Placement;
  minWidth: number;
  initialFocus: 'first' | 'last' | 'none';
  anchorRef: { readonly current: HTMLElement | null };
  className?: string;
  onClose: (restoreFocus: boolean) => void;
}

function MenuPopup({
  id,
  label,
  labelledBy,
  items,
  header,
  placement,
  minWidth,
  initialFocus,
  anchorRef,
  className,
  onClose,
}: MenuPopupProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const typeahead = useRef({ text: '', at: 0 });

  // Into the top layer before measuring (a hidden popover has no size).
  useLayoutEffect(() => {
    const menu = menuRef.current;
    showPopoverSafe(menu);
    return () => hidePopoverSafe(menu);
  }, []);
  useAnchoredPosition(true, anchorRef, menuRef, placement, 6);

  const enabledIndexes = items
    .map((entry, i) => (isAction(entry) && !entry.disabled ? i : -1))
    .filter((i) => i >= 0);

  const focusAt = (index: number | undefined) => {
    if (index === undefined) return;
    itemRefs.current[index]?.focus({ preventScroll: false });
  };

  useLayoutEffect(() => {
    if (initialFocus === 'first') focusAt(enabledIndexes[0]);
    else if (initialFocus === 'last') focusAt(enabledIndexes[enabledIndexes.length - 1]);
    else menuRef.current?.focus({ preventScroll: true });
    // Initial focus only.
  }, []);

  useOutsidePointerDown(true, [menuRef, anchorRef], () => onClose(false));

  // Close when the window loses focus (switching apps) like native menus.
  useEffect(() => {
    const onBlur = () => onClose(false);
    window.addEventListener('blur', onBlur);
    return () => window.removeEventListener('blur', onBlur);
  }, [onClose]);

  const currentIndex = () => itemRefs.current.findIndex((el) => el === document.activeElement);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = currentIndex();
    const pos = enabledIndexes.indexOf(current);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusAt(enabledIndexes[pos < 0 ? 0 : (pos + 1) % enabledIndexes.length]);
        return;
      case 'ArrowUp':
        event.preventDefault();
        focusAt(
          enabledIndexes[pos < 0 ? enabledIndexes.length - 1 : (pos - 1 + enabledIndexes.length) % enabledIndexes.length],
        );
        return;
      case 'Home':
        event.preventDefault();
        focusAt(enabledIndexes[0]);
        return;
      case 'End':
        event.preventDefault();
        focusAt(enabledIndexes[enabledIndexes.length - 1]);
        return;
      case 'Escape':
        // Stop here so an enclosing dialog does not also close.
        event.preventDefault();
        event.stopPropagation();
        onClose(true);
        return;
      case 'Tab':
        onClose(false);
        return;
    }
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey && /\S/.test(event.key)) {
      const now = Date.now();
      const t = typeahead.current;
      t.text = now - t.at > 700 ? event.key.toLowerCase() : t.text + event.key.toLowerCase();
      t.at = now;
      const order = [...enabledIndexes.slice(pos + 1), ...enabledIndexes.slice(0, pos + 1)];
      const match = order.find((i) => textOf(items[i] as MenuActionItem).toLowerCase().startsWith(t.text));
      if (match !== undefined) {
        event.preventDefault();
        focusAt(match);
      }
    }
  };

  /** One entry; `labelId` is set on a label that names a group. */
  function renderEntry(entry: MenuEntry, index: number, labelId?: string): ReactElement {
    const key = entry.id ?? String(index);
    if (entry.type === 'separator') {
      return <div key={key} role="separator" className="my-1 h-px bg-line" />;
    }
    if (entry.type === 'label') {
      return (
        <div
          key={key}
          id={labelId}
          role="presentation"
          className="px-2.5 pt-2 pb-1 text-[11px] font-semibold tracking-[0.06em] text-fg-subtle uppercase"
        >
          {entry.label}
        </div>
      );
    }
    const checkable = entry.checked !== undefined;
    return (
      <button
        key={key}
        ref={(node) => {
          itemRefs.current[index] = node;
        }}
        type="button"
        role={checkable ? (entry.radio ? 'menuitemradio' : 'menuitemcheckbox') : 'menuitem'}
        aria-checked={checkable ? entry.checked : undefined}
        aria-disabled={entry.disabled || undefined}
        tabIndex={-1}
        onClick={() => {
          if (entry.disabled) return;
          if (!entry.keepOpen) onClose(true);
          entry.onSelect();
        }}
        onMouseMove={(e) => {
          // Hover moves focus like a native menu, so keyboard and mouse never disagree about the active item.
          if (!entry.disabled && document.activeElement !== e.currentTarget) e.currentTarget.focus();
        }}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-left text-sm outline-none',
          'focus:bg-surface-3 [&_svg]:size-4 [&_svg]:shrink-0',
          entry.disabled ? 'cursor-not-allowed opacity-45' : 'cursor-pointer',
          entry.destructive ? 'text-danger-text' : 'text-fg',
        )}
      >
        {checkable ? (
          <span className="flex size-4 items-center justify-center">
            {entry.checked && <Check aria-hidden strokeWidth={2.75} />}
          </span>
        ) : entry.icon != null ? (
          <span aria-hidden className={cn('flex', entry.destructive ? 'text-danger-text' : 'text-fg-muted')}>
            {entry.icon}
          </span>
        ) : null}
        {checkable && entry.icon != null && (
          <span aria-hidden className="flex text-fg-muted">
            {entry.icon}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">{entry.label}</span>
          {entry.description != null && (
            <span className="block truncate text-xs text-fg-muted">{entry.description}</span>
          )}
        </span>
        {entry.shortcut != null && <span className="ml-3 shrink-0 text-xs text-fg-subtle">{entry.shortcut}</span>}
      </button>
    );
  }

  // Indexes stay those of the flat `items`, so focus, arrows and typeahead work the same inside a group.
  const children: ReactNode[] = [];
  for (let index = 0; index < items.length; index++) {
    const entry = items[index]!;
    if (entry.type !== 'label' || !entry.group) {
      children.push(renderEntry(entry, index));
      continue;
    }
    const labelId = `${id}-group-${index}`;
    const members = [renderEntry(entry, index, labelId)];
    while (index + 1 < items.length && isAction(items[index + 1]!)) {
      index++;
      members.push(renderEntry(items[index]!, index));
    }
    children.push(
      <div key={`group-${entry.id ?? labelId}`} role="group" aria-labelledby={labelId}>
        {members}
      </div>,
    );
  }

  return (
    <div
      ref={menuRef}
      id={id}
      role="menu"
      aria-label={label}
      aria-labelledby={labelledBy}
      aria-orientation="vertical"
      tabIndex={-1}
      popover="manual"
      onKeyDown={onKeyDown}
      style={{ minWidth, top: 0, left: 0 }}
      className={cn(
        'fixed inset-auto m-0 max-h-[min(420px,calc(100dvh-16px))] max-w-[min(360px,calc(100vw-16px))] overflow-y-auto',
        'rounded-tile border border-line bg-surface p-1.5 text-fg shadow-pop outline-none animate-pop-in gt-scroll',
        'z-[var(--gt-z-popover)]',
        className,
      )}
    >
      {header != null && <div className="border-b border-line px-2.5 pt-1.5 pb-2.5 mb-1">{header}</div>}
      {children}
    </div>
  );
}
